import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {HubConnection, LogLevel} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {environment} from '../../environments/environment';
import {OrganizzazioniService} from './organizzazioni.service';
import {Attivita, Pianifica} from '../_models/Pianificazione';

@Injectable({providedIn: 'root'})
export class PianificazioneRealtimeService {
  attivitaCreated$ = new BehaviorSubject<Attivita | null>(null);
  attivitaUpdated$ = new BehaviorSubject<Attivita | null>(null);
  attivitaDeleted$ = new BehaviorSubject<Attivita | null>(null);

  pianificaCreated$ = new BehaviorSubject<Pianifica | null>(null);
  pianificaUpdated$ = new BehaviorSubject<Pianifica | null>(null);
  pianificaDeleted$ = new BehaviorSubject<Pianifica | null>(null);

  attivitaHubState$ = new BehaviorSubject<number>(HubState.Connecting);
  pianificaHubState$ = new BehaviorSubject<number>(HubState.Connecting);

  private attivitaHub?: HubConnection;
  private pianificaHub?: HubConnection;

  constructor(private organizzazioniService: OrganizzazioniService) {}

  async startConnection(): Promise<void> {
    // Questi hub usano la negoziazione standard (nessuna opzione WebSocket-only)
    // e mantengono in console solo warning/error per non sporcare l'output in sviluppo.
    this.attivitaHub = buildHubConnection(`${environment.localApiUrl}planning/hubs/attivita`, this.attivitaHubState$, {}, LogLevel.Warning);
    this.pianificaHub = buildHubConnection(`${environment.localApiUrl}planning/hubs/pianifiche`, this.pianificaHubState$, {}, LogLevel.Warning);

    this.bindAttivitaEvents();
    this.bindPianificaEvents();

    await Promise.all([
      startHubConnection(this.attivitaHub, this.attivitaHubState$),
      startHubConnection(this.pianificaHub, this.pianificaHubState$),
    ]);
  }

  stopConnection(): void {
    void stopHubConnection(this.attivitaHub);
    void stopHubConnection(this.pianificaHub);
    this.attivitaHubState$.next(HubState.Offline);
    this.pianificaHubState$.next(HubState.Offline);
  }

  private bindAttivitaEvents(): void {
    const hub = this.attivitaHub!;
    const orgMatch = (a: Attivita) => {
      const match = a?.organizzazioneId === this.organizzazioniService.organizzazioneId;
      if (!environment.production) {
        // [DIAG T012e] conferma ricezione evento + esito filtro organizzazione
        console.debug('[RT attivita] evento ricevuto', {
          attivitaId: a?.attivitaId,
          pianificaId: a?.pianificaId,
          orgEvento: a?.organizzazioneId,
          orgClient: this.organizzazioniService.organizzazioneId,
          orgMatch: match
        });
      }
      return match;
    };
    hub.on('SendCreated', (a: Attivita) => { if (orgMatch(a)) this.attivitaCreated$.next(a); });
    hub.on('SendUpdated', (a: Attivita) => { if (orgMatch(a)) this.attivitaUpdated$.next(a); });
    hub.on('SendDeleted', (a: Attivita) => { if (orgMatch(a)) this.attivitaDeleted$.next(a); });
  }

  private bindPianificaEvents(): void {
    const hub = this.pianificaHub!;
    const orgMatch = (p: Pianifica) =>
      p.organizzazioneId === this.organizzazioniService.organizzazioneId;
    hub.on('SendCreated', (p: Pianifica) => { if (orgMatch(p)) this.pianificaCreated$.next(p); });
    hub.on('SendUpdated', (p: Pianifica) => { if (orgMatch(p)) this.pianificaUpdated$.next(p); });
    hub.on('SendDeleted', (p: Pianifica) => { if (orgMatch(p)) this.pianificaDeleted$.next(p); });
  }
}
